import { createRouter, createWebHistory } from 'vue-router'
import { i18n } from '../i18n'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('../views/HomeView.vue'),
      meta: { title: 'titles.home' }
    },
    {
      path: '/products',
      name: 'products',
      component: () => import('../views/ProductsView.vue'),
      meta: { title: 'titles.products' }
    },
    {
      path: '/products/:id',
      name: 'product-detail',
      component: () => import('../views/ProductDetailView.vue'),
      meta: { title: 'titles.productDetail' }
    },
    {
      path: '/category/:slug',
      name: 'category',
      component: () => import('../views/CategoryView.vue'),
      meta: { title: 'titles.category' } // Component updates this
    },
    {
      path: '/cart',
      name: 'cart',
      component: () => import('../views/CartView.vue'),
      meta: { title: 'titles.cart' }
    },
    {
      path: '/checkout',
      name: 'checkout',
      component: () => import('../views/CheckoutView.vue'),
      meta: { title: 'titles.checkout' }
    },
    {
      path: '/search',
      name: 'search',
      component: () => import('../views/SearchView.vue'),
      meta: { title: 'titles.search' }
    },
    {
      path: '/wishlist',
      name: 'wishlist',
      component: () => import('../views/WishlistView.vue'),
      meta: { title: 'titles.wishlist' }
    },
    {
      path: '/about',
      name: 'about',
      component: () => import('../views/AboutView.vue'),
      meta: { title: 'titles.about' }
    },
    {
      path: '/contact',
      name: 'contact',
      component: () => import('../views/ContactView.vue'),
      meta: { title: 'titles.contact' }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('../views/LoginView.vue'),
      meta: { title: 'titles.login' }
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: () => import('../views/ForgotPasswordView.vue'),
      meta: { title: 'forgotPassword.title' }
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('../views/ResetPasswordView.vue'),
      meta: { title: 'forgotPassword.title' }
    },
    {
      path: '/auth/callback',
      name: 'oauth-callback',
      component: () => import('../views/OAuthCallbackView.vue'),
      meta: { title: 'titles.signingIn' }
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('../views/RegisterView.vue'),
      meta: { title: 'titles.register' }
    },
    {
      path: '/privacy',
      name: 'privacy',
      component: () => import('../views/PrivacyPolicyView.vue'),
      meta: { title: 'titles.privacy' }
    },
    {
      path: '/terms',
      name: 'terms',
      component: () => import('../views/TermsView.vue'),
      meta: { title: 'titles.terms' }
    },
    {
      path: '/returns',
      name: 'returns',
      component: () => import('../views/ReturnPolicyView.vue'),
      meta: { title: 'titles.returns' }
    },
    {
      path: '/delivery',
      name: 'delivery',
      component: () => import('../views/DeliveryPolicyView.vue'),
      meta: { title: 'titles.delivery' }
    },
    {
      path: '/account',
      name: 'account',
      component: () => import('../views/AccountView.vue'),
      meta: { requiresAuth: true, title: 'titles.account' },
    },
    {
      path: '/order-success/:orderNumber',
      name: 'order-success',
      component: () => import('../views/OrderSuccessView.vue'),
      meta: { title: 'titles.orderSuccess' }
    },
    {
      path: '/track-service',
      name: 'track-service',
      component: () => import('../views/TrackServiceView.vue'),
      meta: { title: 'titles.trackService' }
    },
    {
      path: '/service/book',
      name: 'book-service',
      component: () => import('../views/BookServiceView.vue'),
      meta: { title: 'titles.bookService' }
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/',
    },
  ],
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  },
})

import { useAuthStore } from '../stores/auth'
import { useSettingsStore } from '../stores/settings'

router.beforeEach((to, _from, next) => {
  const auth = useAuthStore()

  if (to.meta.requiresAuth && !auth.isLoggedIn()) {
    next('/login')
  } else {
    next()
  }
})

router.afterEach((to) => {
  const settingsStore = useSettingsStore()
  const storeName = settingsStore.settings?.storeName || 'Click Electra'

  // Use translation if key exists, otherwise fallback or keep as is
  const titleKey = to.meta.title as string
  const translatedTitle = titleKey ? i18n.global.t(titleKey) : ''

  const pageTitle = translatedTitle ? `${translatedTitle} | ${storeName}` : storeName
  document.title = pageTitle
})

export default router
